export type ClimateZone = 'B' | 'C' | 'D' | 'E' | 'F';

type TempPair = [number, number];

export const ZONE_BY_PREFIX: Record<string, ClimateZone> = {
  '00': 'D', '01': 'D', '02': 'D', '03': 'D', '04': 'C', '05': 'D', '06': 'E', '07': 'C', '08': 'D', '09': 'C',
  '10': 'E', '11': 'F', '12': 'E', '13': 'E', '14': 'E', '15': 'E', '16': 'D', '17': 'D', '18': 'D', '19': 'D',
  '20': 'E', '21': 'E', '22': 'E', '23': 'F', '24': 'E', '25': 'E', '26': 'E', '27': 'E', '28': 'E', '29': 'E',
  '30': 'E', '31': 'E', '32': 'F', '33': 'E', '34': 'E', '35': 'E', '36': 'E', '37': 'E', '38': 'F', '39': 'F',
  '40': 'E', '41': 'E', '42': 'E', '43': 'E', '44': 'E', '45': 'E', '46': 'E', '47': 'E', '48': 'E',
  '50': 'D', '51': 'D', '52': 'E', '53': 'D', '54': 'D', '55': 'D', '56': 'D', '57': 'D', '58': 'D', '59': 'D',
  '60': 'E', '61': 'E', '62': 'E', '63': 'D', '64': 'D', '65': 'D', '66': 'D', '67': 'E',
  '70': 'C', '71': 'C', '72': 'C', '73': 'C', '74': 'C', '75': 'D', '76': 'C',
  '80': 'C', '81': 'C', '82': 'D', '83': 'D', '84': 'C', '85': 'D', '86': 'E', '87': 'C', '88': 'C', '89': 'B',
  '90': 'B', '91': 'B', '92': 'B', '93': 'B', '94': 'C', '95': 'B', '96': 'B', '97': 'B', '98': 'B',
};

/** Netto Conto Termico in euro. Per taglia: [bassa, media]. Bassa = pavimento. Media = termosifoni e ventole. */
const BONUS: Array<Record<ClimateZone, TempPair>> = [
  { B: [2742, 2025], C: [3549, 2621], D: [4517, 3335], E: [5485, 4050], F: [5807, 4288] },
  { B: [3900, 3145], C: [5047, 4070], D: [6424, 5180], E: [7801, 6290], F: [8259, 6660] },
  { B: [4133, 3357], C: [5349, 4345], D: [6808, 5529], E: [8267, 6714], F: [8753, 7109] },
  { B: [4549, 3544], C: [5887, 4586], D: [7492, 5837], E: [9098, 7088], F: [9633, 7505] },
];

export const SIZE_OPTIONS = [
  { value: 'fino a 110', label: 'fino a 110 mq' },
  { value: '110-170', label: '110–170 mq' },
  { value: '170-230', label: '170–230 mq' },
  { value: 'oltre 230', label: 'oltre 230 mq' },
] as const;

export function zoneFromCap(cap: string): ClimateZone | null {
  if (!/^\d{5}$/.test(cap)) return null;
  return ZONE_BY_PREFIX[cap.slice(0, 2)] || null;
}

export function formatEur(value: number): string {
  const formatted = Math.round(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return formatted + ' €';
}

function floorOf(zone: ClimateZone, low: boolean, sizeIndex: number): number {
  return BONUS[sizeIndex][zone][low ? 0 : 1];
}

export function zoneMax(zone: ClimateZone): number {
  return BONUS[BONUS.length - 1][zone][0];
}

/** Prima riga della tabella: taglia non selezionabile. */
const SELECTABLE_FROM = 1;

function eachSelectable(
  zone: ClimateZone,
  visit: (pair: TempPair, index: number) => void
): void {
  for (let index = SELECTABLE_FROM; index < BONUS.length; index++) {
    visit(BONUS[index][zone], index);
  }
}

/** Estremi sulle tre taglie selezionabili e sulle due colonne impianto. Esclude "fino a 110". */
export function zoneBounds(zone: ClimateZone): { min: number; max: number } {
  let min = Infinity;
  let max = 0;
  eachSelectable(zone, (pair) => {
    min = Math.min(min, pair[0], pair[1]);
    max = Math.max(max, pair[0], pair[1]);
  });
  return { min, max };
}

/** Estremi della sola colonna impianto, solo taglie selezionabili. */
export function heatBounds(zone: ClimateZone, heat: string): { min: number; max: number } {
  const column = heat === 'pavimento' ? 0 : 1;
  let min = Infinity;
  let max = 0;
  eachSelectable(zone, (pair) => {
    const value = pair[column];
    min = Math.min(min, value);
    max = Math.max(max, value);
  });
  return { min, max };
}

/**
 * Minimo ancora mostrabile in barra.
 * Senza taglia: minimo dell'intervallo aperto. Con taglia: cella termosifoni/ventole di quella taglia.
 */
export function selectableFloor(zone: ClimateZone, sizeValue?: string): number {
  const index = sizeIndex(sizeValue);
  if (index !== null && index >= SELECTABLE_FROM) return BONUS[index][zone][1];
  return zoneBounds(zone).min;
}

/**
 * Massimo ancora possibile nel testo "fino a".
 * Senza taglia è zoneMax. Con taglia è la cella pavimento di quella sola taglia.
 */
export function remainingMax(zone: ClimateZone, sizeValue?: string): number {
  const index = sizeIndex(sizeValue);
  if (index === null || index < SELECTABLE_FROM) return zoneMax(zone);
  return BONUS[index][zone][0];
}

/**
 * Stringe la fascia verso il centro, restando dentro gli estremi possibili.
 * Ogni passo tiene il 92% della larghezza (circa −8%).
 */
export function shrinkTowardCenter(
  min: number,
  max: number,
  steps: number
): { min: number; max: number } {
  if (steps <= 0 || max <= min) {
    return { min: Math.round(min), max: Math.round(max) };
  }
  const factor = 0.92 ** steps;
  const center = (min + max) / 2;
  const half = ((max - min) * factor) / 2;
  const nextMin = Math.max(min, Math.round(center - half));
  const nextMax = Math.min(max, Math.round(center + half));
  if (nextMax <= nextMin) {
    return { min: Math.round(min), max: Math.round(max) };
  }
  return { min: nextMin, max: nextMax };
}

function split4(total: number): number[] {
  const weights = [8, 3, 5, 13];
  const sumW = weights.reduce((sum, weight) => sum + weight, 0);
  const rounded = Math.round(total);
  const parts = weights.map((weight) => Math.floor((rounded * weight) / sumW));
  parts[parts.length - 1] += rounded - parts.reduce((sum, part) => sum + part, 0);
  return parts;
}

function sizeIndex(sizeValue?: string): number | null {
  const index = SIZE_OPTIONS.findIndex((option) => option.value === sizeValue);
  return index >= 0 ? index : null;
}

export interface ValuationQuote {
  c1: number;
  c2: number;
  c3: number | null;
  value: number | null;
  high: number | null;
}

export function quote(zone: ClimateZone, heat?: string, sizeValue?: string): ValuationQuote {
  const floor = floorOf(zone, false, 0);
  const [c1, c2, c3base] = split4(floor);
  if (!heat) {
    return { c1, c2, c3: null, value: null, high: null };
  }
  const low = heat === 'pavimento';
  const heatedFloor = floorOf(zone, low, 0);
  const c3 = c3base + Math.max(0, heatedFloor - floor);
  const index = sizeIndex(sizeValue);
  if (index === null) {
    return { c1, c2, c3, value: null, high: null };
  }
  const value = floorOf(zone, low, index);
  const high = Math.min(value + 1500, zoneMax(zone));
  return { c1, c2, c3, value, high };
}

export function bonificoOf(value: number, high: number): number {
  return Math.round(((value + high) / 2) * 0.65);
}

export function bonificoBand(value: number, high: number): { low: number; high: number } {
  const base = bonificoOf(value, high);
  return { low: Math.round(base * 0.95), high: Math.round(base * 1.05) };
}
